<template>
  <div class="animated fadeIn">

    <!-- Header -->
    <b-row class="mb-3 align-items-center">
      <b-col>
        <h4 class="mb-0"><i class="fa fa-bug mr-2 text-danger"></i>Issue Tracker</h4>
        <small class="text-muted">Support team dashboard</small>
      </b-col>
      <b-col class="text-right">
        <b-button size="sm" variant="outline-secondary" @click="load" :disabled="loading">
          <i class="icon-refresh"></i> Refresh
        </b-button>
        <b-button size="sm" variant="danger" class="ml-2" to="/issue-tracker/create">
          <i class="icon-plus"></i> New Issue
        </b-button>
      </b-col>
    </b-row>

    <!-- Stat Cards -->
    <b-row class="mb-4">
      <b-col sm="6" lg="3" class="mb-3">
        <b-card class="stat-card border-0 shadow-sm h-100">
          <div class="d-flex align-items-center">
            <div class="stat-icon bg-primary text-white rounded-circle mr-3">
              <i class="icon-folder-alt"></i>
            </div>
            <div>
              <div class="stat-value text-primary">{{ summary.totalOpen || 0 }}</div>
              <div class="stat-label text-muted">Open Issues</div>
            </div>
          </div>
        </b-card>
      </b-col>
      <b-col sm="6" lg="3" class="mb-3">
        <b-card class="stat-card border-0 shadow-sm h-100">
          <div class="d-flex align-items-center">
            <div class="stat-icon bg-warning text-white rounded-circle mr-3">
              <i class="icon-wrench"></i>
            </div>
            <div>
              <div class="stat-value text-warning">{{ summary.totalInProgress || 0 }}</div>
              <div class="stat-label text-muted">In Progress</div>
            </div>
          </div>
        </b-card>
      </b-col>
      <b-col sm="6" lg="3" class="mb-3">
        <b-card class="stat-card border-0 shadow-sm h-100">
          <div class="d-flex align-items-center">
            <div class="stat-icon bg-danger text-white rounded-circle mr-3">
              <i class="icon-fire"></i>
            </div>
            <div>
              <div class="stat-value text-danger">{{ summary.totalCritical || 0 }}</div>
              <div class="stat-label text-muted">Critical</div>
            </div>
          </div>
        </b-card>
      </b-col>
      <b-col sm="6" lg="3" class="mb-3">
        <b-card class="stat-card border-0 shadow-sm h-100">
          <div class="d-flex align-items-center">
            <div class="stat-icon bg-success text-white rounded-circle mr-3">
              <i class="icon-check"></i>
            </div>
            <div>
              <div class="stat-value text-success">{{ summary.resolvedToday || 0 }}</div>
              <div class="stat-label text-muted">Resolved Today</div>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Charts Row -->
    <b-row class="mb-4">
      <b-col md="5" class="mb-3">
        <b-card class="shadow-sm h-100" header="Issues by Status">
          <div style="position:relative;height:220px">
            <canvas ref="doughnutChart"></canvas>
          </div>
        </b-card>
      </b-col>
      <b-col md="7" class="mb-3">
        <b-card class="shadow-sm h-100" header="Issues by Client Type">
          <div style="position:relative;height:220px">
            <canvas ref="barChart"></canvas>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Critical Issues + Workload -->
    <b-row>
      <b-col md="6" class="mb-4">
        <b-card no-body class="shadow-sm">
          <b-card-header class="d-flex justify-content-between align-items-center">
            <span><i class="fa fa-fire text-danger mr-1"></i>Open Critical Issues</span>
            <b-button size="sm" variant="outline-danger" to="/issue-tracker/list">View All</b-button>
          </b-card-header>
          <b-table
            :items="summary.recentCritical || []"
            :fields="criticalFields"
            :busy="loading"
            responsive hover show-empty
            empty-text="No critical issues open."
            class="mb-0"
          >
            <template #table-busy>
              <div class="text-center py-3"><b-spinner small variant="danger" /></div>
            </template>
            <template #cell(title)="d">
              <router-link :to="'/issue-tracker/' + d.item.id" class="font-weight-bold">
                #{{ d.item.id }} {{ d.value }}
              </router-link>
            </template>
            <template #cell(status)="d">
              <b-badge :variant="statusVariant(d.value)" class="badge-sm">{{ d.value }}</b-badge>
            </template>
            <template #cell(assigned_name)="d">
              <span v-if="d.value" class="d-flex align-items-center">
                <span class="avatar-xs mr-1">{{ d.value.charAt(0) }}</span>{{ d.value }}
              </span>
              <span v-else class="text-muted small">Unassigned</span>
            </template>
          </b-table>
        </b-card>
      </b-col>

      <b-col md="6" class="mb-4">
        <b-card no-body class="shadow-sm">
          <b-card-header><i class="fa fa-users mr-1 text-info"></i>Team Workload</b-card-header>
          <b-table
            :items="summary.memberWorkload || []"
            :fields="workloadFields"
            :busy="loading"
            responsive hover show-empty
            empty-text="No assignments yet."
            class="mb-0"
          >
            <template #table-busy>
              <div class="text-center py-3"><b-spinner small variant="info" /></div>
            </template>
            <template #cell(assigned_name)="d">
              <span class="d-flex align-items-center">
                <span class="avatar-xs mr-2">{{ d.value ? d.value.charAt(0) : '?' }}</span>
                <strong>{{ d.value || '—' }}</strong>
              </span>
            </template>
            <template #cell(openCount)="d">
              <b-badge :variant="d.value > 3 ? 'danger' : d.value > 1 ? 'warning' : 'success'">
                {{ d.value }}
              </b-badge>
            </template>
          </b-table>
        </b-card>
      </b-col>
    </b-row>

  </div>
</template>

<script>
import Chart from 'chart.js'
import issueService from '@/services/issueService'

export default {
  name: 'IssueTrackerDashboard',
  data: function() {
    return {
      loading: false,
      summary: {},
      doughnutInst: null,
      barInst: null,
      criticalFields: [
        { key: 'title',         label: 'Issue' },
        { key: 'status',        label: 'Status' },
        { key: 'assigned_name', label: 'Assignee' },
      ],
      workloadFields: [
        { key: 'assigned_name',  label: 'Member' },
        { key: 'openCount',      label: 'Open',     sortable: true },
        { key: 'resolvedCount',  label: 'Resolved', sortable: true },
        { key: 'total',          label: 'Total',    sortable: true },
      ]
    }
  },
  created: function() { this.load() },
  beforeDestroy: function() {
    if (this.doughnutInst) this.doughnutInst.destroy()
    if (this.barInst)      this.barInst.destroy()
  },
  methods: {
    load: function() {
      var self = this
      self.loading = true
      issueService.getSummary(
        function(data) {
          self.summary = data
          self.loading = false
          self.$nextTick(function() { self.renderCharts() })
        },
        function(err) {
          self.loading = false
          self.$bvToast.toast(err, { title: 'Error', variant: 'danger', solid: true })
        }
      )
    },
    renderCharts: function() {
      this.renderDoughnut()
      this.renderBar()
    },
    renderDoughnut: function() {
      var ctx = this.$refs.doughnutChart
      if (!ctx) return
      var statusData = this.summary.byStatus || []
      var labels = statusData.map(function(s) { return s.status })
      var counts = statusData.map(function(s) { return s.cnt })
      var palette = {
        OPEN: '#007bff', IN_PROGRESS: '#ffc107', PENDING: '#17a2b8',
        RESOLVED: '#28a745', CLOSED: '#6c757d', REOPENED: '#dc3545'
      }
      var colors = labels.map(function(l) { return palette[l] || '#adb5bd' })
      if (this.doughnutInst) {
        this.doughnutInst.data.labels   = labels
        this.doughnutInst.data.datasets[0].data            = counts
        this.doughnutInst.data.datasets[0].backgroundColor = colors
        this.doughnutInst.update()
        return
      }
      this.doughnutInst = new Chart(ctx, {
        type: 'doughnut',
        data: { labels: labels, datasets: [{ data: counts, backgroundColor: colors, borderWidth: 2 }] },
        options: {
          responsive: true, maintainAspectRatio: false,
          legend: { position: 'right' },
          cutoutPercentage: 65
        }
      })
    },
    renderBar: function() {
      var ctx = this.$refs.barChart
      if (!ctx) return
      var clientData = this.summary.byClientType || []
      var labels = clientData.map(function(c) { return c.client_type })
      var counts = clientData.map(function(c) { return c.cnt })
      var palette = { HRM: '#007bff', CS: '#28a745', ADMIN: '#6f42c1', OTHER: '#fd7e14' }
      var colors = labels.map(function(l) { return palette[l] || '#adb5bd' })
      if (this.barInst) {
        this.barInst.data.labels   = labels
        this.barInst.data.datasets[0].data            = counts
        this.barInst.data.datasets[0].backgroundColor = colors
        this.barInst.update()
        return
      }
      this.barInst = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: labels,
          datasets: [{ label: 'Issues', data: counts, backgroundColor: colors, borderRadius: 4 }]
        },
        options: {
          responsive: true, maintainAspectRatio: false,
          legend: { display: false },
          scales: {
            yAxes: [{ ticks: { beginAtZero: true, stepSize: 1 } }]
          }
        }
      })
    },
    statusVariant: function(s) {
      var map = { OPEN: 'primary', IN_PROGRESS: 'warning', PENDING: 'info',
                  RESOLVED: 'success', CLOSED: 'secondary', REOPENED: 'danger' }
      return map[s] || 'secondary'
    }
  }
}
</script>

<style scoped>
.stat-card { transition: transform .15s, box-shadow .15s; }
.stat-card:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,0,0,.1) !important; }
.stat-icon { width: 44px; height: 44px; display:flex; align-items:center; justify-content:center; font-size:1.2rem; flex-shrink:0; }
.stat-value { font-size: 1.9rem; font-weight: 700; line-height: 1; }
.stat-label { font-size: .78rem; text-transform: uppercase; letter-spacing: .05em; margin-top: 2px; }
.avatar-xs { width:26px; height:26px; border-radius:50%; background:#007bff; color:#fff;
             display:inline-flex; align-items:center; justify-content:center;
             font-size:.75rem; font-weight:700; flex-shrink:0; }
.badge-sm { font-size: .7rem; }
</style>
